// Dark Store — CP routing page (tl-app → partner-apis-cs).
// Base path `/leader/ds-routing`. Contract: docs/cp-features/
// darkstore-api-contract.md §3b in the citymall-services repo — that file is
// the source of truth for these shapes. Every response uses the house
// envelope `{ status, data }`; callers read `response.data.data`.

import { notifyError } from "@components/ds/notify";
import {
  getHandoffConflict,
  getRerackConflict,
} from "@screens/DsRouting/helpers";
import { api } from "../api";

/**
 * @typedef {Object} DsRoutingDb
 * @property {number} cp_db_id
 * @property {string} name
 * @property {string} route_id
 */

/**
 * @typedef {Object} DsRoutingTp
 * @property {string} cx_id
 * @property {string|null} cx_name
 * @property {string|null} area
 * @property {string} date YYYY-MM-DD order_edd (IST)
 * @property {"D0"|"NEXT"|null} scope
 * @property {boolean} is_d0
 * @property {number} order_count
 * @property {number} shipment_count
 * @property {string[]} racks rack numbers, e.g. ["B-04"]
 * @property {(DsRoutingDb|null)} db
 * @property {"NOT_RECEIVED"|"NOT_RACKED"|"PACKED"|"ROUTED"|"PICKED"|"OFD"} status
 * @property {string|null} [lock] LIVE_OFD | DELIVERED_TODAY | PICKED_BY_DB
 * @property {boolean} [pending_assign] racked (PACKED), no DB
 * @property {boolean} [pending_ofd] has a DB, ROUTED or PICKED, not OFD
 * @property {{cp_db_id: number, name: string}|null} [boxes_with] another DB holds the boxes after a hand-off
 * @property {number} [boxes_in_hand] boxes the picking DB still holds: re-racked before the TP moves
 * @property {boolean} [ofd_ready] By DB only: picked and markable now
 */

/** `GET /summary` → `{ date, d0_start, d0: {to_route, routed, total}, next: {total} }` */
export const getDsRoutingSummary = () => api.get("/leader/ds-routing/summary");

/**
 * `GET /tps?scope=D0|NEXT|ALL&locality=` →
 * `{ scope, date, d0_start, progress, localities: [{name, count}], tps: DsRoutingTp[] }`
 */
export const getDsRoutingTps = ({ scope = "D0", locality = null } = {}) =>
  api.get("/leader/ds-routing/tps", {
    params: locality ? { scope, locality } : { scope },
  });

/**
 * `GET /tps/:cx_id/dbs` — the "Add DB" sheet, nearby DBs first →
 * `{ cx_id, current_db_id, live_positions, dbs: [{cp_db_id, name, phone_masked, tps_today, distance_km, nearby}] }`
 */
export const getDsRoutingDbs = (cx_id) =>
  api.get(`/leader/ds-routing/tps/${cx_id}/dbs`);

/**
 * `GET /tps/:cx_id/recommended` — unrouted D0 TPs nearest this one →
 * `{ parent: {cx_id, cx_name, area, db, status}, tps: [{cx_id, cx_name, area, order_count, status, distance_km}] }`
 */
export const getDsRoutingRecommended = (cx_id) =>
  api.get(`/leader/ds-routing/tps/${cx_id}/recommended`);

// A TP_PICKED_BY_DB 400 is the screen's cue for the hand-off confirm, and a
// TP_RERACK_REQUIRED 400 for the re-rack sheet, not errors to toast: the shared interceptor would snackbar every 400, so these
// calls let a 400 through as a response and handle it here. Any other 400 is
// toasted with the server's message (as the interceptor would) and thrown.
const acceptBadRequest = (status) =>
  (status >= 200 && status < 300) || status === 400;

const postTpChange = async (path, body) => {
  const response = await api.post(path, body, {
    validateStatus: acceptBadRequest,
  });
  if (
    response?.status === 400 &&
    !getHandoffConflict(response) &&
    !getRerackConflict(response)
  ) {
    const data = response.data || {};
    if (data.message) notifyError(data.message, "");
    const error = new Error(data.error || "BAD_REQUEST");
    error.response = response;
    throw error;
  }
  return response;
};

/**
 * `POST /tps/assign` — put TPs on a DB's route (Add DB / Change). Resolves
 * with a 400 TP_RERACK_REQUIRED response while a DB still holds picked boxes
 * (re-rack them, then retry), or, on a server without that gate, a 400
 * TP_PICKED_BY_DB (retry with `confirm_handoff: true`). `confirm_handoff` is always sent: the server lets
 * a request without the key through unasked, for app builds older than the
 * hand-off confirm.
 */
export const assignDsRoutingTps = ({ cx_ids, cp_db_id, confirm_handoff }) =>
  postTpChange("/leader/ds-routing/tps/assign", {
    cx_ids,
    cp_db_id,
    confirm_handoff: Boolean(confirm_handoff),
  });

/** `POST /tps/unassign` — take TPs off their route (same hand-off 400 as assign). */
export const unassignDsRoutingTps = ({ cx_ids, cp_db_id, confirm_handoff }) =>
  postTpChange("/leader/ds-routing/tps/unassign", {
    cx_ids,
    cp_db_id,
    confirm_handoff: Boolean(confirm_handoff),
  });

/** `POST /tps/:cx_id/ofd` — Mark OFD on the TP's DB. */
export const markDsRoutingTpOfd = ({ cx_id, cp_db_id }) =>
  api.post(`/leader/ds-routing/tps/${cx_id}/ofd`, { cp_db_id });

// ---- By DB (OFD and un-assignment) ------------------------------------------

/**
 * `GET /dbs` → `{ date, dbs: [{ cp_db_id, name, phone_masked, total, routed,
 *   picked, ofd, ofd_ready }] }` (DBs with at least one TP today)
 */
export const getDsRoutingDbList = () => api.get("/leader/ds-routing/dbs");

/**
 * `GET /dbs/:cp_db_id/tps` → `{ cp_db_id, name, multi_trip_enabled,
 *   tps: [DsRoutingTp + ofd_ready] }`
 */
export const getDsRoutingDbTps = (cp_db_id) =>
  api.get(`/leader/ds-routing/dbs/${cp_db_id}/tps`);

/**
 * `POST /dbs/:cp_db_id/ofd` — Mark OFD for several TPs at once →
 * `{ done: [cx_id], failed: [{ cx_id, code }] }`
 */
export const markDsRoutingDbOfd = ({ cp_db_id, cx_ids }) =>
  api.post(`/leader/ds-routing/dbs/${cp_db_id}/ofd`, { cx_ids });

// ---- Returned to store (re-racking) -----------------------------------------

/**
 * `GET /returns` → `{ boxes_to_rerack, dbs: [{ cp_db_id|null, name|null,
 *   to_rerack, reracked_today, on_trip, states: {STATE: n},
 *   customers: [{ cx_id, cx_name, address, state, order_edd, on_trip,
 *   boxes: [{ shipment_id, last_rack_no }] }] }] }`
 */
export const getDsReturns = () => api.get("/leader/ds-routing/returns");

/** `POST /returns/rerack` — put one returned box on a rack. */
export const rerackDsBox = ({ shipment_id, rack_id, scan_kind }) =>
  api.post("/leader/ds-routing/returns/rerack", {
    shipment_id,
    rack_id,
    scan_kind,
  });
